<template>
    <div class="lini-view-container">
        <DxLoadPanel :visible="isPrinting" message="Se generează PDF-ul..." :show-overlay="true" :show-pane="true" />
        <div v-if="document" class="doc-header-card">
            <div class="doc-header-top">
                <div class="doc-header-main">
                    <i class="dx-icon dx-icon-doc doc-hdr-icon" />
                    <span class="doc-hdr-numar">#{{ document.Numar }}</span>
                    <span class="doc-hdr-categorie-badge">{{ document.Categorie }}</span>
                </div>
                <div class="doc-header-actions">
                    <DxButton v-if="document.ComandaID" text="Preia din comandă" icon="import" styling-mode="text"
                        :disabled="isPreluandProduse" @click="onPreiaDinComanda" />
                    <DxButton v-if="document.PartenerID && document.ProiectID" text="Preia din comenzile proiectului"
                        icon="import" styling-mode="text" :disabled="isPreluandProduse" @click="onDeschidePreiaDinComenziProiect" />
                    <DxButton v-if="areDocumentTip" :text="document.Document ? `Document asociat: ${document.Document}` : 'Document asociat'"
                        icon="link" styling-mode="text" @click="onDeschideDocAsociatPopup" />
                    <DxButton v-if="areDocumentTip && document.Document" icon="remove" hint="Elimină legătura cu documentul asociat"
                        styling-mode="text" @click="onEliminaDocumentAsociat" />
                    <DxButton v-if="areDocumentTip && document.Document" text="Preia linii" icon="import" styling-mode="text"
                        :disabled="isPreluandProduse" @click="onPreiaLiniiDinDocumentAsociat" />
                    <DxButton icon="print" hint="Tipărire PDF" styling-mode="text" @click="onTiparirePDF" />
                    <DxButton icon="share" hint="Partajează" styling-mode="text" @click="shareClicked" />
                    <DxButton text="Editează document" icon="edit" styling-mode="text" @click="$emit('open-edit-document')" />
                </div>
            </div>
            <div class="doc-meta-strip">
                <div class="doc-meta-item doc-meta-item--partener">
                    <i class="dx-icon dx-icon-group doc-meta-icon" />
                    <div class="doc-meta-content">
                        <span class="doc-meta-label">Partener</span>
                        <span class="doc-meta-value doc-meta-value--partener" :title="document.Partener">{{ document.Partener || '—' }}</span>
                    </div>
                </div>
                <div class="doc-meta-divider" />
                <div class="doc-meta-item">
                    <i class="dx-icon dx-icon-event doc-meta-icon" />
                    <div class="doc-meta-content">
                        <span class="doc-meta-label">Data</span>
                        <span class="doc-meta-value">{{ formatDate(document.Data) }}</span>
                    </div>
                </div>
                <div class="doc-meta-divider" />
                <div class="doc-meta-item">
                    <i class="dx-icon dx-icon-tags doc-meta-icon" />
                    <div class="doc-meta-content">
                        <span class="doc-meta-label">Tip</span>
                        <span class="doc-meta-value">{{ document.Tip }}</span>
                    </div>
                </div>
                <template v-if="document.Scadenta">
                    <div class="doc-meta-divider" />
                    <div class="doc-meta-item">
                        <i class="dx-icon dx-icon-clock doc-meta-icon" />
                        <div class="doc-meta-content">
                            <span class="doc-meta-label">Scadență</span>
                            <span class="doc-meta-value">{{ formatDate(document.Scadenta) }}</span>
                        </div>
                    </div>
                </template>
                <template v-if="document.Curs && document.Valuta && document.Valuta !== 'RON'">
                    <div class="doc-meta-divider" />
                    <div class="doc-meta-item">
                        <i class="dx-icon dx-icon-money doc-meta-icon" />
                        <div class="doc-meta-content">
                            <span class="doc-meta-label">Curs {{ document.Valuta }}</span>
                            <span class="doc-meta-value">{{ document.Curs?.toLocaleString('ro-RO', { minimumFractionDigits: 4 }) }}</span>
                        </div>
                    </div>
                </template>
                <div class="doc-meta-spacer" />
                <template v-if="cursEUR !== null || cursUSD !== null">
                    <div class="doc-cursuri-box">
                        <span class="doc-meta-label">Cursuri BNR</span>
                        <div class="doc-cursuri-rows">
                            <div v-if="cursEUR !== null" class="doc-curs-row">
                                <span class="doc-curs-simbol">EUR</span>
                                <span class="doc-curs-valoare">{{ formatRO(cursEUR) }}</span>
                            </div>
                            <div v-if="cursUSD !== null" class="doc-curs-row">
                                <span class="doc-curs-simbol">USD</span>
                                <span class="doc-curs-valoare">{{ formatRO(cursUSD) }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="doc-meta-divider" style="margin: 0 10px;" />
                </template>
                <div class="doc-totals-box" v-if="document.Total || document.Valoare">
                    <div class="doc-totals-row" v-if="document.Valoare">
                        <span class="doc-totals-label">Fără TVA</span>
                        <span class="doc-totals-value-secondary">{{ document.Valoare.toLocaleString('ro-RO', { minimumFractionDigits: 2 }) }}&nbsp;{{ document.Valuta || 'RON' }}</span>
                    </div>
                    <div class="doc-totals-separator" v-if="document.Valoare && document.Total" />
                    <div class="doc-totals-row" v-if="document.Total">
                        <span class="doc-totals-label doc-totals-label--main">
                            <i class="dx-icon dx-icon-money" style="font-size:11px;vertical-align:middle;margin-right:3px" />
                            Total
                        </span>
                        <span class="doc-totals-value-main">{{ document.Total.toLocaleString('ro-RO', { minimumFractionDigits: 2 }) }}&nbsp;{{ document.Valuta || 'RON' }}</span>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="document" class="grid-section">
            <GridDocumentLinii
                ref="gridLiniiRef"
                :document="document"
                @refresh-document="$emit('refresh-document', $event)"
                @has-linii-changed="onHasLiniiChanged"
                @dirty-count-change="$emit('dirty-count-change', $event)"
                @open-etichete="onOpenEtichete"
            />
        </div>

        <div v-if="document === null" class="null-document-state">
            <i class="dx-icon-warning"></i>
            <p>Vă rugăm să selectați un document pentru a vedea detaliile.</p>
        </div>

        <PopupEtichete v-if="document" :document="document" :linie-id="eticheteLinieId"
            :visible="isEticheteVisible" @update:visible="isEticheteVisible = $event" />

        <DxPopup :visible="isPreiaComenziPopupVisible" @update:visible="isPreiaComenziPopupVisible = $event"
            :width="600" :height="450" :show-title="true" title="Alegeți comenzile pentru preluare produse"
            :drag-enabled="true" :hide-on-outside-click="true">
            <DxDataGrid :data-source="dsComenzi" key-expr="Id" height="100%" :show-borders="true"
                @selection-changed="onComenziProiectSelectionChanged">
                <DxSelection mode="multiple" show-check-boxes-mode="always" />
                <DxColumn data-field="Cod" caption="Cod" width="140" />
                <DxColumn data-field="DataComenzii" caption="Data" data-type="date" format="yyyy-MM-dd" width="120" />
                <DxColumn data-field="Nume" caption="Denumire" />
            </DxDataGrid>
            <div class="form-footer footer-modern">
                <div class="footer-right">
                    <DxButton text="Importă selectate" type="success" styling-mode="outlined"
                        :disabled="comenziProiectSelectate.length === 0 || isPreluandProduse" @click="onImportaComenziSelectate" />
                    <DxButton text="Închide" type="danger" styling-mode="outlined"
                        @click="isPreiaComenziPopupVisible = false" />
                </div>
            </div>
        </DxPopup>

        <DxPopup :visible="isDocAsociatPopupVisible" @update:visible="isDocAsociatPopupVisible = $event"
            :width="600" :height="480" :show-title="true" title="Selectează documentul asociat"
            :drag-enabled="true" :hide-on-outside-click="true">
            <DxTextBox v-model:value="docAsociatSearchText" placeholder="Caută după număr document..."
                @value-changed="onCautaDocumenteAsociate" />
            <DxDataGrid :data-source="docAsociatCandidati" key-expr="Id" height="360px" :show-borders="true"
                @row-dbl-click="onSelecteazaDocumentAsociat">
                <DxColumn data-field="Numar" caption="Număr" width="140" />
                <DxColumn data-field="Data" caption="Data" data-type="date" format="yyyy-MM-dd" width="120" />
                <DxColumn data-field="Tip" caption="Tip" />
            </DxDataGrid>
            <div class="form-footer footer-modern">
                <div class="footer-right">
                    <DxButton text="Închide" type="danger" styling-mode="outlined"
                        @click="isDocAsociatPopupVisible = false" />
                </div>
            </div>
        </DxPopup>

    </div>
</template>
<script lang="ts">
import { locale, loadMessages } from 'devextreme/localization';
import {
    DxToolbar,
    DxToolbarItem,
    DxColumn,
    DxColumnChooser,
    DxDataGrid,
    DxEditing,
    DxExport,
    DxFilterRow,
    DxGrouping,
    DxGroupPanel,
    DxHeaderFilter,
    DxLoadPanel,
    DxPager,
    DxPaging,
    DxRemoteOperations,
    DxScrolling,
    DxSearchPanel,
    DxSelection,
    DxSorting,
    DxStateStoring,
    DxTexts,
    DxItem,
    DxForm as DxFormEdit,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue/button';
import { DxCheckBox, DxScrollView, DxPopup, DxTextBox } from 'devextreme-vue';
import DataSource from "devextreme/data/data_source";
import AppSetting from '@/settings/appSettings';
import { DxForm, DxSimpleItem, DxGroupItem, DxLabel, DxEmptyItem } from "devextreme-vue/form"
import DxTextArea from 'devextreme-vue/text-area'
import { readonly, version } from 'vue';
import notify from 'devextreme/ui/notify'
import { confirm } from 'devextreme/ui/dialog';
import { defineComponent, ref } from 'vue';
import DOC_Documente from '@/services/Documente/DOC_Documente';
import DOC_Linii from '@/services/Documente/DOC_Linii';
import http from '@/settings/apiSettings';
import GridDocumentLinii from './GridDocumentLinii.vue';
import PopupEtichete from './PopupEtichete.vue';
export default defineComponent({
    name: 'LiniView',
    components: {
        DxButton,
        PopupEtichete,
        DxToolbar,
        DxToolbarItem,
        DxScrollView,
        DxColumn,
        DxColumnChooser,
        DxDataGrid,
        DxEditing,
        DxExport,
        DxFilterRow,
        DxGrouping,
        DxGroupPanel,
        DxHeaderFilter,
        DxLoadPanel,
        DxPager,
        DxPaging,
        DxRemoteOperations,
        DxScrolling,
        DxSearchPanel,
        DxSelection,
        DxSorting,
        DxStateStoring,
        DxTexts,
        DxForm,
        DxSimpleItem,
        DxTextArea,
        DxGroupItem,
        DxCheckBox,
        DxLabel,
        DxEmptyItem,
        DxItem,
        DxFormEdit,
        GridDocumentLinii,
        DxPopup,
        DxTextBox,
    },
    props: {
        document: Object,
        sidebarOpen: { type: Boolean, default: false },
    },
    data() {
        return {
            cursEUR: null as number | null,
            cursUSD: null as number | null,
            isReadonly: true,
            isDisabled: true,
            partenerNou: {
                ID: null,
                Nume: this.document?.Partener,
                Cod: this.document?.PartenerCod,
                CUI: this.document?.PartenerCUI,
                GrupPartener: this.document?.GrupPartener
            },
            categorieDocument: null,
            tipDocument: null,

            dsCategorieDocumente: null,
            dsTipDocumente: null,
            dsAgent: null,
            dsZone: null,
            dsNumereContracte: null,
            dsPartener: null,
            dsGrupePartener: null,
            dsComenzi: null,
            dsProiecte: null,
            dsGestiune: null,
            dsValute: null,
            areLinii: false,
            isPrinting: false,
            isEticheteVisible: false,
            eticheteLinieId: null as string | null,
            isPreluandProduse: false,
            isPreiaComenziPopupVisible: false,
            comenziProiectSelectate: [] as string[],
            isDocAsociatPopupVisible: false,
            docAsociatSearchText: '',
            docAsociatCandidati: [] as any[],
            areDocumentTip: false,
        }
    },
    methods: {
        onOpenEtichete(payload: { linieId: string | null }) {
            this.eticheteLinieId = payload?.linieId ?? null;
            this.isEticheteVisible = true;
        },
        async importaProduseDinComenzi(comandaIds: string[]) {
            this.isPreluandProduse = true;
            try {
                const resp = await DOC_Linii.preiaDinComenzi(this.document.Id, comandaIds);
                const { ProduseImportate, ComenziProcesate } = resp.data;
                if (ProduseImportate > 0) {
                    notify(`Au fost importate ${ProduseImportate} produse din ${ComenziProcesate} comandă/comenzi.`, 'success', 4000);
                } else {
                    notify('Niciun produs importat.', 'warning', 3000);
                }
                await (this.$refs.gridLiniiRef as any)?.reload();
            } catch (err: any) {
                const msg = err?.response?.data?.message || err?.message || 'Eroare necunoscută';
                notify('Eroare la preluarea produselor: ' + msg, 'error', 4000);
            } finally {
                this.isPreluandProduse = false;
            }
        },
        async onPreiaDinComanda() {
            if (!this.document?.ComandaID) return;
            const confirmat = await confirm('Doriți încărcarea produselor din comandă?', 'Atenție');
            if (!confirmat) return;
            await this.importaProduseDinComenzi([this.document.ComandaID]);
        },
        async onDeschidePreiaDinComenziProiect() {
            if (!this.document?.PartenerID || !this.document?.ProiectID) {
                notify('Alegeți partenerul și proiectul pentru a identifica comanda.', 'warning', 4000);
                return;
            }
            const confirmat = await confirm('Doriți încărcarea produselor asociate proiectului?', 'Atenție');
            if (!confirmat) return;

            const ds = this.dsComenzi as any;
            ds.filter([['ClientId', '=', this.document.PartenerID], 'and', ['ProiectId', '=', this.document.ProiectID]]);
            const comenzi = await ds.load();

            if (!comenzi || comenzi.length === 0) {
                notify('Nu este nimic de încărcat.', 'info', 3000);
                return;
            }
            if (comenzi.length === 1) {
                await this.importaProduseDinComenzi([comenzi[0].Id]);
                return;
            }
            this.comenziProiectSelectate = [];
            this.isPreiaComenziPopupVisible = true;
        },
        onComenziProiectSelectionChanged(e: any) {
            this.comenziProiectSelectate = e.selectedRowKeys;
        },
        async onImportaComenziSelectate() {
            if (this.comenziProiectSelectate.length === 0) return;
            this.isPreiaComenziPopupVisible = false;
            await this.importaProduseDinComenzi(this.comenziProiectSelectate);
        },
        async fetchAreDocumentTip() {
            if (!this.document?.TipId) return;
            try {
                const resp = await http.clientDocumenteOdata.get(`DOC_Tipuri(${this.document.TipId})?$select=AreDocument`);
                this.areDocumentTip = resp.data?.AreDocument === true;
            } catch (err) {
                console.error('Eroare la verificarea AreDocument pe tipul documentului:', err);
            }
        },
        async onDeschideDocAsociatPopup() {
            this.docAsociatSearchText = '';
            this.isDocAsociatPopupVisible = true;
            await this.onCautaDocumenteAsociate();
        },
        async onCautaDocumenteAsociate() {
            try {
                const resp = await DOC_Documente.getDocumenteAsociateCandidati(this.document.Id, this.docAsociatSearchText);
                this.docAsociatCandidati = resp.data;
            } catch (err: any) {
                notify('Eroare la căutarea documentelor: ' + (err?.response?.data?.message || err?.message), 'error', 4000);
            }
        },
        async onSelecteazaDocumentAsociat(e: any) {
            const candidat = e.data;
            try {
                await DOC_Documente.asociazaDocument(this.document.Id, candidat.Id);
                notify(`Document asociat: ${candidat.Numar}`, 'success', 3000);
                this.isDocAsociatPopupVisible = false;
                this.$emit('refresh-document', this.document.Id);
            } catch (err: any) {
                notify('Eroare la asociere: ' + (err?.response?.data?.message || err?.message), 'error', 4000);
            }
        },
        async onEliminaDocumentAsociat() {
            const confirmat = await confirm('Legătura cu documentul asociat va fi eliminată. Continuați?', 'Atenție');
            if (!confirmat) return;
            try {
                await DOC_Documente.dezasociazaDocument(this.document.Id);
                notify('Legătura a fost eliminată.', 'success', 3000);
                this.$emit('refresh-document', this.document.Id);
            } catch (err: any) {
                notify('Eroare la eliminarea legăturii: ' + (err?.response?.data?.message || err?.message), 'error', 4000);
            }
        },
        async onPreiaLiniiDinDocumentAsociat() {
            const confirmat = await confirm('Doriți încărcarea liniilor din documentul asociat?', 'Atenție');
            if (!confirmat) return;
            this.isPreluandProduse = true;
            try {
                const resp = await DOC_Linii.preiaDinDocumentAsociat(this.document.Id);
                const { ProduseImportate } = resp.data;
                notify(ProduseImportate > 0 ? `Au fost importate ${ProduseImportate} linii.` : 'Nicio linie importată.',
                    ProduseImportate > 0 ? 'success' : 'warning', 4000);
                await (this.$refs.gridLiniiRef as any)?.reload();
            } catch (err: any) {
                notify('Eroare la preluarea liniilor: ' + (err?.response?.data?.message || err?.message), 'error', 4000);
            } finally {
                this.isPreluandProduse = false;
            }
        },
        formatRO(val: number | null | undefined): string {
            if (val == null) return '';
            return val.toLocaleString('ro-RO', { maximumFractionDigits: 10 });
        },
        async loadCursuriValutare() {
            try {
                const [respEUR, respUSD] = await Promise.all([
                    http.clientComercialOdata.get(`ERP_CursValutar?$filter=Simbol eq 'EUR'&$orderby=Data desc&$top=1&$select=Curs`),
                    http.clientComercialOdata.get(`ERP_CursValutar?$filter=Simbol eq 'USD'&$orderby=Data desc&$top=1&$select=Curs`),
                ]);
                this.cursEUR = respEUR.data?.value?.[0]?.Curs ?? null;
                this.cursUSD = respUSD.data?.value?.[0]?.Curs ?? null;
            } catch { /* ignore */ }
        },
        onHasLiniiChanged(hasLinii: boolean) {
            this.areLinii = hasLinii;
        },
        onGridSaved() {
            this.$emit('refresh-document', this.document.Id);
        },
        async onTiparirePDF() {
            if (this.isPrinting) return;
            this.isPrinting = true;
            try {
                const token = localStorage.getItem('ERA_Jwt') ?? '';
                const isGuid = (s: string | null | undefined): boolean =>
                    !!s && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

                // 1. Fetch DOC_Tipuri — report identifiers + template settings per document type
                let tipRaport: any = null;
                if (this.document.TipId) {
                    try {
                        const tipResp = await http.clientDocumenteOdata.get(
                            `DOC_Tipuri(${this.document.TipId})?$select=ClasaRaport,ClasaRaport2,TitluRaport,AreFundal,RaportAntetAreTel,RaportAntetAreFax,RaportAntetAreEmail,RaportAntetAreWww`
                        );
                        tipRaport = tipResp.data;
                    } catch { /* ignore */ }
                }

                // 2. Try DevExpress report from Rapoarte module
                //    Backend lookup order: ERP_ReportsDesigner.ID → ERP_Reports.ID → ERP_Reports.nume
                //    Tries ClasaRaport first, then ClasaRaport2 as alternative layout
                const tryDevExpressReport = async (clasaRaport: string | null | undefined): Promise<boolean> => {
                    if (!clasaRaport) return false;
                    try {
                        const params = new URLSearchParams({ clasaRaport, documentID: this.document.Id });
                        const url = `${AppSetting.API_Url_Rapoarte}report/GetReportByClasaRaport?${params}`;
                        const resp = await fetch(url, { headers: { Authorization: 'Bearer ' + token } });
                        if (resp.ok && resp.headers.get('content-type')?.includes('application/pdf')) {
                            const blob = await resp.blob();
                            const blobUrl = URL.createObjectURL(blob);
                            const win = window.open(blobUrl, '_blank');
                            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                            if (!win) notify('Permiteți pop-up-uri pentru a vizualiza documentul.', 'warning', 4000);
                            return true;
                        }
                    } catch { /* ignore */ }
                    return false;
                };

                if (await tryDevExpressReport(tipRaport?.ClasaRaport)) return;
                if (await tryDevExpressReport(tipRaport?.ClasaRaport2)) return;

                // 3. HTML fallback — generate printable page in browser using DOC_Tipuri template settings
                const docId = this.document.Id;

                let emitent: any = null;
                if (this.document.StructuraId) {
                    try {
                        const sResp = await http.clientDocumenteOdata.get(
                            `ERP_StructuraCompanie(${this.document.StructuraId})?$select=PartenerId`
                        );
                        const partenerId = sResp.data?.PartenerId;
                        if (partenerId) {
                            const pResp = await http.clientDocumenteOdata.get(
                                `PAR_Parteneri(${partenerId})?$select=Nume,CUI,Numar,Strada,Localitate,Tel,Fax,Banca,Cont`
                            );
                            emitent = pResp.data;
                        }
                    } catch { /* ignore */ }
                }

                let cumparator: any = null;
                if (this.document.PartenerId) {
                    try {
                        const pResp = await http.clientDocumenteOdata.get(
                            `PAR_Parteneri(${this.document.PartenerId})?$select=Nume,CUI,Numar,Strada,Localitate,Banca,Cont`
                        );
                        cumparator = pResp.data;
                    } catch { /* ignore */ }
                }

                const liniiResp = await http.clientDocumenteOdata.get(
                    `DOC_Linii?$filter=DocumentId eq ${docId}&$expand=Produs($select=Cod,Nume),Unitate($select=Simbol)&$orderby=DataCreare asc`
                );
                const linii: any[] = liniiResp.data?.value ?? [];

                const html = this.buildInvoiceHtml(emitent, cumparator, linii, tipRaport);

                const win = window.open('', '_blank');
                if (!win) { notify('Permiteți pop-up-uri pentru a vizualiza documentul.', 'warning', 4000); return; }
                win.document.open();
                win.document.write(html);
                win.document.close();

            } catch (err: any) {
                notify('Eroare la generarea preview: ' + (err?.message ?? 'Eroare'), 'error', 4000);
            } finally {
                this.isPrinting = false;
            }
        },

        buildInvoiceHtml(emitent: any, cumparator: any, linii: any[], tipRaport: any = null): string {
            const fmt = (n: any) => n != null ? Number(n).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00';
            const fmtDate = (d: any) => d ? new Date(d).toLocaleDateString('ro-RO') : '-';
            const esc = (s: any) => (s ?? '').toString()
                .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

            // TitluRaport from DOC_Tipuri overrides the generic document type name
            const titlu = esc(tipRaport?.TitluRaport || this.document.Tip || 'Document');
            const numar = esc(this.document.Numar ?? '-');
            const valuta = esc(this.document.Valuta ?? 'RON');

            // RaportAntet* flags from DOC_Tipuri control what contact info to show (null = show)
            const showTel = tipRaport?.RaportAntetAreTel !== false;
            const showFax = tipRaport?.RaportAntetAreFax !== false;

            const partyHtml = (party: any, label: string, isEmitent = false) => {
                if (!party) return `<div class="party"><div class="party-title">${label}</div><div class="party-name">-</div></div>`;
                let rows = `<div class="party-title">${label}</div><div class="party-name">${esc(party.Nume)}</div>`;
                if (party.CUI) rows += `<div class="party-row">CUI: ${esc(party.CUI)}</div>`;
                if (party.Numar) rows += `<div class="party-row">Nr. Reg. Com.: ${esc(party.Numar)}</div>`;
                if (party.Strada) rows += `<div class="party-row">${esc(party.Strada)}</div>`;
                if (party.Localitate) rows += `<div class="party-row">${esc(party.Localitate)}</div>`;
                if (isEmitent && showTel && party.Tel) rows += `<div class="party-row">Tel: ${esc(party.Tel)}</div>`;
                if (isEmitent && showFax && party.Fax) rows += `<div class="party-row">Fax: ${esc(party.Fax)}</div>`;
                if (party.Banca) rows += `<div class="party-row">Banca: ${esc(party.Banca)}</div>`;
                if (party.Cont) rows += `<div class="party-row">Cont: ${esc(party.Cont)}</div>`;
                return `<div class="party">${rows}</div>`;
            };

            let liniiRows = '';
            linii.forEach((l: any, i: number) => {
                liniiRows += `<tr>
                    <td class="nr">${i + 1}</td>
                    <td class="cod">${esc(l.Produs?.Cod ?? '-')}</td>
                    <td>${esc(l.Produs?.Nume ?? l.Nume ?? '-')}</td>
                    <td class="um">${esc(l.Unitate?.Simbol ?? '-')}</td>
                    <td class="num">${fmt(l.Cantitate)}</td>
                    <td class="num">${fmt(l.PretUnitar)}</td>
                    <td class="num">${(l.Tva ?? 0)}%</td>
                    <td class="num">${fmt(l.Valoare)}</td>
                    <td class="num bold">${fmt(l.Total)}</td>
                </tr>`;
            });

            const scadenta = this.document.Scadenta
                ? ` &nbsp;|&nbsp; Scaden&#x21b;&#x103;: <strong>${fmtDate(this.document.Scadenta)}</strong>`
                : '';
            const obs = this.document.Observatii
                ? `<div class="obs">Obs: ${esc(this.document.Observatii)}</div>`
                : '';
            const accize = this.document.Accize && this.document.Accize !== 0
                ? `<div class="totals-row"><span class="totals-label">Accize:</span><span>${fmt(this.document.Accize)} ${valuta}</span></div>`
                : '';
            const cursRand = this.document.Valuta && this.document.Valuta !== 'RON' && this.document.Curs
                ? `<div class="obs">Curs valutar: 1 ${valuta} = ${Number(this.document.Curs).toLocaleString('ro-RO', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} RON</div>`
                : '';

            return `<!DOCTYPE html><html lang="ro"><head><meta charset="utf-8"/>
<title>${titlu} ${numar}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:9pt;color:#1e293b;background:#fff;padding:16px}
.page{max-width:210mm;margin:0 auto;padding:6mm 10mm}
h1{font-size:18pt;font-weight:700;text-align:center;text-transform:uppercase;letter-spacing:1px;margin-bottom:2mm}
.doc-meta{text-align:center;font-size:9pt;color:#475569;margin-bottom:6mm}
.parties{display:flex;gap:6mm;margin-bottom:5mm}
.party{flex:1;border:1px solid #e5e7eb;border-radius:4px;padding:3mm 4mm;font-size:8.5pt}
.party-title{font-size:7.5pt;font-weight:700;text-transform:uppercase;color:#94a3b8;letter-spacing:.5px;margin-bottom:2mm;border-bottom:1px solid #f1f5f9;padding-bottom:1mm}
.party-name{font-weight:700;font-size:10pt;margin-bottom:1mm}
.party-row{color:#475569;margin-bottom:.5mm}
table{width:100%;border-collapse:collapse;font-size:8pt;margin-bottom:4mm}
thead th{background:#1e293b;color:#fff;padding:2.5mm 2mm;text-align:left;font-weight:600;font-size:7.5pt}
thead th.num{text-align:right}thead th.um{text-align:center}
tbody tr:nth-child(even){background:#f8f9fc}
tbody td{padding:2mm;border-bottom:1px solid #e5e7eb;vertical-align:top}
td.num{text-align:right;white-space:nowrap}
td.nr{text-align:center;width:8mm;color:#64748b}
td.cod{width:22mm;font-family:monospace;font-size:8pt}
td.um{width:10mm;text-align:center}
.bold{font-weight:700}
.totals{display:flex;justify-content:flex-end;margin-bottom:4mm}
.totals-box{border:1px solid #e5e7eb;border-radius:4px;padding:3mm 5mm;min-width:64mm;font-size:9pt}
.totals-row{display:flex;justify-content:space-between;gap:10mm;padding:1.2mm 0;border-bottom:1px solid #f1f5f9}
.totals-row:last-child{border-bottom:none;font-weight:700;font-size:10pt;padding-top:2mm;color:#059669}
.totals-label{color:#475569}
.sign-area{display:flex;gap:8mm;margin-top:8mm;font-size:8pt;color:#475569}
.sign-box{flex:1;text-align:center;border-top:1px solid #94a3b8;padding-top:2mm}
.obs{font-size:8pt;color:#475569;margin-bottom:4mm;padding:2mm 3mm;background:#f8f9fc;border-left:3px solid #e5e7eb;border-radius:2px}
.print-btn{margin-top:14px;text-align:center}
.print-btn button{padding:8px 22px;background:#ff2c21;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:10pt;font-family:Arial,sans-serif}
@media print{.print-btn{display:none}body{padding:0}.page{padding:4mm 6mm}@page{size:A4 portrait;margin:8mm}}
</style></head><body><div class="page">
<h1>${titlu}</h1>
<div class="doc-meta">Nr. <strong>${numar}</strong> &nbsp;|&nbsp; Data: <strong>${fmtDate(this.document.Data)}</strong>${scadenta}</div>
<div class="parties">${partyHtml(emitent, 'Furnizor', true)}${partyHtml(cumparator, 'Cump&#x103;r&#x103;tor')}</div>
${obs}
<table><thead><tr>
<th class="nr">Nr.</th><th class="cod">Cod</th><th>Denumire</th>
<th class="um">UM</th><th class="num">Cantitate</th><th class="num">Pre&#x21a; unitar</th>
<th class="num">TVA %</th><th class="num">Valoare</th><th class="num">Total</th>
</tr></thead><tbody>${liniiRows}</tbody></table>
<div class="totals"><div class="totals-box">
<div class="totals-row"><span class="totals-label">Valoare f&#x103;r&#x103; TVA:</span><span>${fmt(this.document.Valoare)} ${valuta}</span></div>
<div class="totals-row"><span class="totals-label">TVA:</span><span>${fmt(this.document.ValoareTva)} ${valuta}</span></div>
${accize}<div class="totals-row"><span class="totals-label">TOTAL:</span><span>${fmt(this.document.Total)} ${valuta}</span></div>
</div></div>
${cursRand}
<div class="sign-area">
<div class="sign-box">Furnizor<br/><br/><br/></div>
<div class="sign-box">Cump&#x103;r&#x103;tor<br/><br/><br/></div>
<div class="sign-box">Gestionar<br/><br/><br/></div>
</div>
<div class="print-btn"><button onclick="window.print()">Tip&#x103;re&#x219;te / Salveaz&#x103; PDF</button></div>
</div></body></html>`;
        },

        buildAvizPdf(pdf: any, linii: any[], titluRaport: string, cuPreturi: boolean) {
            const pageW = 210, marginL = 14, marginR = 14, contentW = 182;
            const fmt = (n: any) => n != null ? Number(n).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
            const fmtDate = (d: any) => d ? new Date(d).toLocaleDateString('ro-RO') : '';
            let y = 18;

            pdf.setFontSize(16); pdf.setFont('helvetica', 'bold');
            pdf.text(titluRaport.toUpperCase(), pageW / 2, y, { align: 'center' });
            y += 7;
            pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
            pdf.text(`Nr. ${this.document.Numar ?? '—'}   |   Data: ${fmtDate(this.document.Data)}`, pageW / 2, y, { align: 'center' });
            y += 10;

            pdf.setFillColor(248, 249, 252);
            pdf.rect(marginL, y, contentW, 20, 'F');
            pdf.setFontSize(8); pdf.setFont('helvetica', 'bold');
            pdf.text('DESTINATAR:', marginL + 3, y + 5);
            pdf.setFont('helvetica', 'normal');
            pdf.text(this.document.Partener ?? '—', marginL + 3, y + 10);
            if (this.document.PartenerCUI) pdf.text(`CUI: ${this.document.PartenerCUI}`, marginL + 3, y + 15);
            y += 24;

            const cols = cuPreturi
                ? [
                    { header: 'Nr.', width: 10, align: 'left' },
                    { header: 'Cod', width: 22, align: 'left' },
                    { header: 'Denumire', width: 71, align: 'left' },
                    { header: 'UM', width: 12, align: 'left' },
                    { header: 'Cant.', width: 18, align: 'right' },
                    { header: 'Preț unitar', width: 25, align: 'right' },
                    { header: 'Total', width: 24, align: 'right' },
                  ]
                : [
                    { header: 'Nr.', width: 10, align: 'left' },
                    { header: 'Cod', width: 25, align: 'left' },
                    { header: 'Denumire', width: 100, align: 'left' },
                    { header: 'UM', width: 18, align: 'left' },
                    { header: 'Cant.', width: 29, align: 'right' },
                  ];

            y = this.drawPdfTableHeader(pdf, cols, marginL, y, contentW);
            y = this.drawPdfTableRows(pdf, cols, linii, marginL, y, contentW, (l: any, i: number) =>
                cuPreturi
                    ? [String(i + 1), l.Produs?.Cod ?? '—', (l.Produs?.Nume || l.Nume || '—').substring(0, 42), l.Unitate?.Simbol ?? '—', fmt(l.Cantitate), fmt(l.PretUnitar), fmt(l.Total)]
                    : [String(i + 1), l.Produs?.Cod ?? '—', (l.Produs?.Nume || l.Nume || '—').substring(0, 55), l.Unitate?.Simbol ?? '—', fmt(l.Cantitate)]
            );

            pdf.setDrawColor(200, 200, 200);
            pdf.line(marginL, y, marginL + contentW, y);
            if (cuPreturi && this.document.Total) {
                y += 6;
                const totX = pageW - marginR - 60;
                pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
                pdf.text('TOTAL:', totX, y);
                pdf.text(`${fmt(this.document.Total)} ${this.document.Valuta ?? 'RON'}`, pageW - marginR, y, { align: 'right' });
            }
        },

        buildDocumentGenericPdf(pdf: any, linii: any[], titluRaport: string) {
            const pageW = 210, marginL = 14, marginR = 14, contentW = 182;
            const fmt = (n: any) => n != null ? Number(n).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
            const fmtDate = (d: any) => d ? new Date(d).toLocaleDateString('ro-RO') : '';
            let y = 18;

            pdf.setFontSize(16); pdf.setFont('helvetica', 'bold');
            pdf.text(titluRaport.toUpperCase(), pageW / 2, y, { align: 'center' });
            y += 7;
            pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
            pdf.text(`Nr. ${this.document.Numar ?? '—'}   |   Data: ${fmtDate(this.document.Data)}`, pageW / 2, y, { align: 'center' });
            y += 10;

            if (this.document.Partener) {
                pdf.setFillColor(248, 249, 252);
                pdf.rect(marginL, y, contentW, 14, 'F');
                pdf.setFontSize(8); pdf.setFont('helvetica', 'normal');
                pdf.text(this.document.Partener, marginL + 3, y + 8);
                y += 18;
            }

            const cols = [
                { header: 'Nr.', width: 10, align: 'left' },
                { header: 'Cod', width: 25, align: 'left' },
                { header: 'Denumire', width: 95, align: 'left' },
                { header: 'UM', width: 15, align: 'left' },
                { header: 'Cant.', width: 20, align: 'right' },
                { header: 'Total', width: 17, align: 'right' },
            ];
            y = this.drawPdfTableHeader(pdf, cols, marginL, y, contentW);
            y = this.drawPdfTableRows(pdf, cols, linii, marginL, y, contentW, (l: any, i: number) => [
                String(i + 1), l.Produs?.Cod ?? '—',
                (l.Produs?.Nume || l.Nume || '—').substring(0, 52),
                l.Unitate?.Simbol ?? '—', fmt(l.Cantitate), fmt(l.Total),
            ]);

            if (this.document.Total) {
                y += 6;
                const totX = pageW - marginR - 50;
                pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
                pdf.text('TOTAL:', totX, y);
                pdf.text(`${fmt(this.document.Total)} ${this.document.Valuta ?? 'RON'}`, pageW - marginR, y, { align: 'right' });
            }
        },

        drawPdfTableHeader(pdf: any, cols: any[], marginL: number, y: number, contentW: number): number {
            pdf.setFillColor(40, 40, 40);
            pdf.rect(marginL, y, contentW, 7, 'F');
            pdf.setFontSize(7.5); pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(255, 255, 255);
            let x = marginL + 2;
            for (const col of cols) {
                col.align === 'right'
                    ? pdf.text(col.header, x + col.width - 2, y + 4.8, { align: 'right' })
                    : pdf.text(col.header, x, y + 4.8);
                x += col.width;
            }
            pdf.setTextColor(0, 0, 0);
            return y + 7;
        },

        drawPdfTableRows(pdf: any, cols: any[], linii: any[], marginL: number, y: number, contentW: number, getVals: (l: any, i: number) => string[]): number {
            pdf.setFont('helvetica', 'normal'); pdf.setFontSize(7.5);
            linii.forEach((linie, idx) => {
                if (y > 270) { pdf.addPage(); y = 15; }
                if (idx % 2 === 0) {
                    pdf.setFillColor(248, 249, 252);
                    pdf.rect(marginL, y, contentW, 6, 'F');
                }
                const vals = getVals(linie, idx);
                let x = marginL + 2;
                for (let i = 0; i < cols.length; i++) {
                    cols[i].align === 'right'
                        ? pdf.text(vals[i] ?? '', x + cols[i].width - 2, y + 4, { align: 'right' })
                        : pdf.text(vals[i] ?? '', x, y + 4);
                    x += cols[i].width;
                }
                y += 6;
            });
            return y;
        },
        async shareClicked() {
            const textToCopy = `${window.location.origin}` + `/documente?id=${this.document.Id}`

            if (!textToCopy) {
                notify('Nu există ID de document de copiat.', 'warning', 5000);
                return;
            }

            try {
                await navigator.clipboard.writeText(textToCopy);
                notify('Adresa URL a fost copiată', 'success', 5000);
            } catch (err) {
                console.error('Eroare la copiere:', err);
                notify('Eroare la copiere. Copiere manuală necesară.', 'error', 5000);
            }
        },
        goBack() {
            this.$router.push('/documente')
            this.$emit('back')
        },
        updateReadOnly() {
            if (this.isReadonly === true) {
                this.isReadonly = false
                this.isDisabled = false
            }
            else {
                this.isReadonly = true
                this.isDisabled = true
            }
        },
        // onValueChangedCategorie(e: any) {
        //     let _filter;
        //     if (e.value != null) {
        //         _filter = ["CategorieId", "=", e.value];
        //     } else {
        //         _filter = null;
        //     }
        //     this.dsTipDocumente.filter(_filter);
        //     this.dsTipDocumente.load();
        // },
        onTipDocumentSelected(e: any) {
            this.document.TipId = e.value
            this.dsTipDocumente.store().byKey(this.document.TipId).then((tip) => {
                this.dsCategorieDocumente.store().byKey(tip.CategorieId).then((categorie) => {
                    this.categorieDocument = categorie
                })
            })
        },
        onPartenerSelected(e: any) {
            this.partenerNou.ID = e.selectedItem.ID
            this.partenerNou.Nume = e.selectedItem.Nume
            this.partenerNou.CUI = e.selectedItem.CUI
            this.partenerNou.Cod = e.selectedItem.Cod

            this.dsGrupePartener.filter("Id", "=", e.selectedItem.GrupID)
            this.dsGrupePartener.load().then((data: any[]) => {
                if (data && data.length > 0)
                    this.partenerNou.GrupPartener = data[0].Nume
            })
        },
        onPartenerCUISelected(e: any) {
            this.partenerNou.ID = e.selectedItem.ID
            this.partenerNou.Nume = e.selectedItem.Nume
            this.partenerNou.CUI = e.selectedItem.CUI
            this.partenerNou.Cod = e.selectedItem.Cod

            this.dsGrupePartener.filter("Id", "=", e.selectedItem.GrupID)
            this.dsGrupePartener.load().then((data: any[]) => {
                if (data && data.length > 0)
                    this.partenerNou.GrupPartener = data[0].Nume
            })
        },
        EditDocumentClicked() {
            this.isReadonly = false;
        },
        async SaveDocumentClicked() {
            try {
                if (!this.document?.Id) {
                    notify("Lipsește Id-ul documentului.", "error", 3000);
                    return;
                }

                // Payload curat: DOAR proprietăți care există în DOC_Documente (EF)
                const payload = {
                    TipId: this.document.TipId ?? null,
                    Numar: this.document.Numar ?? null,
                    Data: this.document.Data ?? null,
                    DataDoc: this.document.DataDoc ?? null,
                    Scadenta: this.document.Scadenta ?? null,

                    GestiuneIntrareId: this.document.GestiuneIntrareId ?? null,
                    GestiuneIesireId: this.document.GestiuneIesireId ?? null,

                    Document: this.document.Document ?? null,
                    ValutaId: this.document.ValutaId ?? null,
                    Curs: this.document.Curs ?? this.document.PretCurs ?? null,
                    PretValutaId: this.document.PretValutaId ?? null,
                    PretCurs: this.document.PretCurs ?? this.document.Curs ?? null,

                    Tva: this.document.Tva ?? null,
                    Valoare: this.document.Valoare ?? null,
                    ValoareTva: this.document.ValoareTva ?? null,
                    Accize: this.document.Accize ?? null,
                    Total: this.document.Total ?? null,

                    Explicatie: this.document.Explicatie ?? null,
                    Observatii: this.document.Observatii ?? null,

                    PartenerId: this.document.PartenerId ?? null,
                    ContractNumar: this.document.ContractNumar ?? null,
                    ContractId: this.document.ContractId ?? null,

                    Adaos: this.document.Adaos ?? null,
                    AdaosValoare: this.document.AdaosValoare ?? null,

                    AreCaf: this.document.AreCaf ?? null,
                    ProcentCaf: this.document.ProcentCaf ?? null,
                    ValoareCaf: this.document.ValoareCaf ?? null,

                    AgentId: this.document.AgentId ?? null,
                    LocDepozitare: this.document.LocDepozitare ?? null,

                    StornatRetur: this.document.StornatRetur ?? null,
                    Autofacturat: this.document.Autofacturat ?? null,
                    EmisTert: this.document.EmisTert ?? null,
                    TvaLaIncasare: this.document.TvaLaIncasare ?? null,

                    ZonaId: this.document.ZonaId ?? null,

                    // dacă ai în UI:
                    ComandaId: this.document.ComandaId ?? null,
                    ProiectId: this.document.ProiectId ?? null,
                    StructIdEditabil: this.document.StructIdEditabil ?? null,
                    StructuraId: this.document.StructuraId ?? null,
                    NotaId: this.document.NotaId ?? null
                };

                console.log("PATCH payload (clean):", payload);

                await DOC_Documente.update(this.document.Id, payload);

                notify("Documentul a fost salvat cu succes.", "success", 2000);
                this.isReadonly = true;
            } catch (error) {
                console.error("Eroare la salvarea documentului:", error);
                const msg =
                    error?.response?.data?.error?.message ||
                    error?.response?.data?.message ||
                    error?.response?.data?.title ||
                    error?.message ||
                    "Eroare la salvare.";
                notify(msg, "error", 3000);
            }
        },
        headerEditClicked() {
            this.isReadonly = false;
        },
        headerSaveClicked() {
            this.submitForm();
            this.isReadonly = true;
        },
        closeClicked() {
            this.goBack();
        },
        submitForm() {
            // Logic to save the whole document form
        },
        formatDate(date: any) {
            if (!date) return '';
            const d = new Date(date);
            return d.toLocaleDateString('ro-RO');
        },
    },
    mounted() {
        this.fetchAreDocumentTip()
        this.loadCursuriValutare()
        this.dsCategorieDocumente = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + 'DOC_Categorii',
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        this.dsTipDocumente = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + `DOC_Tipuri`,
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            }
        });
        this.dsTipDocumente.store().byKey(this.document.TipId).then((tip) => {
            this.dsCategorieDocumente.store().byKey(tip.CategorieId).then((categorie) => {
                this.categorieDocument = categorie
            })
        })
        this.dsAgent = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + 'COM_Agenti',
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")

                    };
                }
            },
        });
        this.dsZone = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + 'ERP_Zone',
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        this.dsNumereContracte = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_ComercialOdata + 'COM_Contracte',
                key: 'ID',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
            select: [
                'Id',
                'NumarContract'
            ]
        });
        this.dsPartener = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + 'PAR_Parteneri',
                key: 'ID',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    }
                }
            }
        });
        this.dsGrupePartener = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + 'PAR_Grupuri',
                key: 'ID',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        this.dsProiecte = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + `DOC_Proiecte`,
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        this.dsGestiune = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + `GES_Gestiuni`,
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        this.dsValute = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_ComercialOdata + `ERP_Valute`,
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        this.dsComenzi = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + `PRO_Comenzi`,
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        loadMessages({
            "ro": {
                "Yes": "Da",
                "No": "Nu",
            }
        });

        locale('ro');
    }
});
</script>
<style>
.lini-view-container {
    padding: 12px 20px;
    background: #fdfdfd;
    height: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    position: relative;
    overflow: hidden;
}

.grid-section {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.grid-section > div {
    flex: 1;
    min-height: 0;
    overflow: hidden;
}

/* ── Document Header Card ── */
.doc-header-card {
    flex-shrink: 0;
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin: 0 0 6px;
}

.doc-header-top {
    padding: 4px 14px;
    background: linear-gradient(90deg, #ff2c21, #ff6a4a);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.doc-header-main {
    display: flex;
    align-items: center;
    gap: 6px;
}

.doc-hdr-icon {
    font-size: 13px;
    color: #fff;
}

.doc-hdr-numar {
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 0.3px;
    line-height: 1.1;
}

.doc-hdr-categorie-badge {
    font-size: 10px;
    font-weight: 600;
    background: rgba(255, 255, 255, 0.2);
    color: white;
    padding: 1px 8px;
    border-radius: 12px;
    letter-spacing: 0.3px;
}

.doc-header-actions {
    display: flex;
    align-items: center;
    gap: 4px;
}

.doc-header-actions .dx-button {
    color: #fff !important;
    min-height: unset !important;
    height: 20px !important;
    width: 20px !important;
}

.doc-header-actions .dx-button.dx-button-has-text {
    width: auto !important;
}

.doc-header-actions .dx-button.dx-button-has-text .dx-button-content {
    padding: 1px 7px !important;
    font-size: 11px !important;
}

.doc-header-actions .dx-button .dx-icon {
    font-size: 12px !important;
    width: 12px !important;
    height: 12px !important;
}

.doc-meta-strip {
    display: flex;
    align-items: center;
    padding: 6px 16px;
    gap: 0;
    flex-wrap: nowrap;
    border-top: 1px solid #f1f5f9;
    overflow: hidden;
}

.doc-meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 12px 0 0;
}

.doc-meta-divider {
    width: 1px;
    height: 22px;
    background: #e5e7eb;
    margin-right: 12px;
    flex-shrink: 0;
}

.doc-meta-icon {
    font-size: 12px;
    color: #64748b;
}

.doc-meta-content {
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.doc-meta-label {
    font-size: 10px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    line-height: 1;
}

.doc-meta-value {
    font-size: 12px;
    font-weight: 500;
    color: #334155;
    line-height: 1.2;
}

.doc-meta-item--partener {
    flex-shrink: 1;
    min-width: 0;
    max-width: 200px;
}

.doc-meta-value--partener {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    max-width: 180px;
}

.doc-meta-spacer {
    flex: 1;
}

.doc-cursuri-box {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 4px 8px;
    background: linear-gradient(135deg, #eff6ff, #eef2ff);
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    min-width: 80px;
    box-shadow: 0 1px 3px rgba(37, 99, 235, 0.08);
}

.doc-cursuri-rows {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.doc-curs-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 8px;
}

.doc-curs-simbol {
    font-size: 9px;
    font-weight: 700;
    color: #2563eb;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    line-height: 1;
}

.doc-curs-valoare {
    font-size: 11px;
    font-weight: 600;
    color: #1e40af;
    white-space: nowrap;
}

.doc-totals-box {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 5px 14px;
    background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
    border: 1px solid #bbf7d0;
    border-radius: 10px;
    min-width: 170px;
    box-shadow: 0 1px 3px rgba(5, 150, 105, 0.08);
}

.doc-totals-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 14px;
}

.doc-totals-label {
    font-size: 10px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    line-height: 1;
}

.doc-totals-label--main {
    color: #065f46;
}

.doc-totals-value-secondary {
    font-size: 11px;
    font-weight: 500;
    color: #475569;
    white-space: nowrap;
}

.doc-totals-value-main {
    font-size: 13px;
    font-weight: 700;
    color: #059669;
    white-space: nowrap;
}

.doc-totals-separator {
    height: 1px;
    background: #d1fae5;
    margin: 1px 0;
}

/* Grid & Empty State (Condensed) */
:deep(.dx-datagrid) {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #edf2f7;
}


.no-lines-empty-state,
.null-document-state {
    text-align: center;
    padding: 40px;
    background: white;
    border-radius: 12px;
    border: 1px dashed #e2e8f0;
    color: #718096;
    margin-top: 12px;
}

.truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}


.dx-form .dx-form-group:has(.dx-form-group-caption) {
    border: 2px solid #ff6600 !important;
    border-radius: 8px !important;
    padding: 20px !important;
    margin-bottom: 25px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
    min-height: 320px;
    margin-left: 30px !important;
}

.dx-field-item {
    padding-top: 6px !important;
    padding-bottom: 0px !important;
    padding-left: 5px !important;
    padding-right: 0px !important;
    margin: 0px !important;
}

.dx-form-group-caption {
    color: #ff6600;
    font-weight: bold;
}

.buttons {
    margin-top: 40px;
}


.cantitate-numberbox {
    margin-left: 130px;
    margin-top: 23px;
}

.form-footer.footer-modern {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 12px 16px;
}

.form-footer .footer-right {
    display: flex;
    gap: 12px;
}
</style>
